import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, ChevronRight, Clock, RotateCcw } from 'lucide-react';
import {
  cargarPreguntas,
  rendirExamen,
  type PreguntaExamen,
  type ResultadoExamen,
} from './academyApi';
import { tituloNumerado } from './academyFormato';
import { BotonCertificado } from './certificado/BotonCertificado';
import { RevisionRespuestas } from './RevisionRespuestas';
import { scrollAlTope } from './scrollAlTope';
import { useAcademia } from './useAcademia';
import { useCurso } from './useAcademia';
import { useEsperaExamen } from './useEsperaExamen';
import { EsqueletoAcademia } from './EsqueletoAcademia';

/**
 * El examen de un curso — opción múltiple, una pregunta por pantalla
 * (ADR-2026-08-24-examen-una-pregunta-por-pantalla).
 *
 * **La corrección la hace el servidor** (ADR-2026-08-25-la-academia-vive-en-supabase):
 * las preguntas llegan sin cuál es la correcta, y `rendirExamen` devuelve el
 * puntaje ya calculado más las correcciones. El front no puede ni corregir ni
 * mentir sobre el resultado.
 *
 * Se corrige AL FINAL y no pregunta por pregunta: con la corrección inmediata, el
 * examen se convierte en un tutorial —la correcta de la 3 muchas veces contesta
 * la 7— y deja de medir nada.
 */
export const PantallaExamen = () => {
  const { slug } = useParams<{ slug: string }>();
  const { cursos, curso, progreso, cargando } = useCurso(slug);

  if (cargando) return <EsqueletoAcademia />;
  if (!curso || !progreso) return <Navigate to="/academia" replace />;

  return <VistaExamen curso={curso} cursos={cursos} progreso={progreso} />;
};

function VistaExamen({
  curso,
  cursos,
  progreso,
}: {
  curso: ReturnType<typeof useAcademia>['cursos'][number];
  cursos: ReturnType<typeof useAcademia>['cursos'];
  progreso: NonNullable<ReturnType<typeof useCurso>['progreso']>;
}) {
  const navigate = useNavigate();
  const raiz = useRef<HTMLDivElement>(null);

  const [preguntas, setPreguntas] = useState<PreguntaExamen[] | null>(null);
  const [respuestas, setRespuestas] = useState<(number | null)[]>([]);
  const [indice, setIndice] = useState(0);
  const [resultado, setResultado] = useState<ResultadoExamen | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * La espera sale del intento **recién rendido** cuando hay uno, y del progreso
   * sólo mientras todavía no se rindió nada en esta pantalla.
   *
   * `progreso` se carga al montar y no se vuelve a pedir: apenas se corrige el
   * examen queda viejo —no conoce el intento que acaba de entrar— y el contador
   * daba en cero. El resultado mostraba "Repetir examen" habilitado sobre un
   * examen que el servidor iba a rechazar por la espera de dos horas
   * (ADR-2026-08-25-la-espera-del-resultado-sale-del-intento-recien-rendido).
   *
   * `rendidoAt` lo devuelve la propia función que corrigió, así que es el mismo
   * instante contra el que después va a medir el servidor.
   */
  const espera = useEsperaExamen(
    resultado ? new Date(resultado.rendidoAt).getTime() : progreso.ultimoIntento,
    curso.esperaMinutos,
  );

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const p = await cargarPreguntas(curso.id);
        if (!vivo) return;
        setPreguntas(p);
        setRespuestas(p.map(() => null));
      } catch (e) {
        console.error('[academia] no se pudieron cargar las preguntas', e);
        if (vivo) setError('No pudimos cargar el examen. Probá de nuevo.');
      }
    })();
    return () => {
      vivo = false;
    };
  }, [curso.id]);

  // Las mismas reglas que aplica el servidor, del lado de la URL: sin esto,
  // escribir `/examen` a mano saltea las lecciones o la espera. Que rebote acá es
  // cortesía —el servidor rechaza igual—, pero evita mostrar un examen que no se
  // va a poder entregar. `resultado` exceptúa: el intento recién rendido arrancó
  // su propia espera y sin esto la pantalla se expulsaría a sí misma.
  if (!progreso.completo) return <Navigate to={`/academia/${curso.slug}`} replace />;
  if (!resultado && !espera.listo) {
    return <Navigate to={`/academia/${curso.slug}`} replace />;
  }

  const corregir = async () => {
    if (!preguntas || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await rendirExamen(
        curso.id,
        preguntas.map((p, i) => ({ preguntaId: p.id, elegida: respuestas[i] })),
      );
      setResultado(r);
      scrollAlTope(raiz.current);
    } catch (e: any) {
      console.error('[academia] no se pudo corregir', e);
      // El servidor manda un mensaje útil cuando rechaza por sus reglas
      // (lecciones incompletas, espera en curso); se muestra tal cual.
      setError(e?.message ?? 'No pudimos corregir el examen. Probá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const reintentar = () => {
    if (!espera.listo || !preguntas) return;
    setRespuestas(preguntas.map(() => null));
    setIndice(0);
    setResultado(null);
    setError(null);
    scrollAlTope(raiz.current);
  };

  if (resultado) {
    return (
      <Resultado
        curso={curso}
        resultado={resultado}
        onReintentar={reintentar}
        espera={espera}
      />
    );
  }

  if (error && !preguntas) {
    return (
      <div className="mx-auto max-w-content px-4 pb-24 pt-2 sm:px-8 md:pb-8">
        <p className="mt-10 text-[14.5px] text-red-700">{error}</p>
      </div>
    );
  }

  if (!preguntas) return <EsqueletoAcademia />;

  const pregunta = preguntas[indice];
  const elegida = respuestas[indice];
  const esUltima = indice === preguntas.length - 1;

  return (
    <div ref={raiz} className="mx-auto max-w-content px-4 pb-24 pt-2 sm:px-8 md:pb-8">
      <div>
        {/* Mismo criterio que el back del curso: en mobile lo pone el shell
            (ADR-2026-08-25-en-mobile-el-back-es-el-del-shell). Desde `md`, que es
            donde el flotante desaparece. */}
        <button
          type="button"
          onClick={() => navigate(`/academia/${curso.slug}`)}
          className="mb-4 hidden items-center gap-1.5 text-[13.5px] font-medium text-black/50 transition-colors hover:text-brand-primary md:inline-flex"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al curso
        </button>

        <h1 className="text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-brand-black/90 sm:text-[32px]">
          Examen — {tituloNumerado(curso, cursos)}
        </h1>
      </div>

      {/* Sin tope de ancho: usa toda la columna de la sección
          (ADR-2026-08-24-examen-a-ancho-completo). Lo que evita el barrido largo
          por opción no es angostar la página sino repartir las opciones en dos
          columnas a partir de `xl`. */}
      <div className="mt-6">
        <div className="mb-5">
          <div className="flex items-baseline justify-between text-[13px] font-medium text-black/50">
            <span>
              Pregunta {indice + 1} de {preguntas.length}
            </span>
            <span>{curso.puntajeMinimo}% para aprobar</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/[0.07]">
            <div
              className="h-full rounded-full bg-brand-primary transition-[width] duration-300"
              style={{ width: `${((indice + 1) / preguntas.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="rounded-2xl border border-black/[0.06] bg-white p-5 sm:p-6">
          <h2 className="text-[18px] font-semibold leading-snug tracking-[-0.01em] text-brand-black/90">
            {pregunta.enunciado}
          </h2>

          {/* `role="radiogroup"`: son opciones excluyentes de una misma pregunta,
              y sin esto un lector de pantalla las anuncia como botones sueltos.
              Dos columnas desde `xl`: a ancho completo, una sola columna deja
              renglones de más de 120 caracteres para opciones de diez palabras. */}
          <div
            className="mt-4 grid gap-2.5 xl:grid-cols-2"
            role="radiogroup"
            aria-label={pregunta.enunciado}
          >
            {pregunta.opciones.map((opcion, i) => {
              const seleccionada = elegida === i;
              return (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={seleccionada}
                  onClick={() =>
                    setRespuestas((prev) =>
                      prev.map((r, k) => (k === indice ? i : r)),
                    )
                  }
                  className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
                    seleccionada
                      ? 'border-brand-primary bg-[#EBEFFE]'
                      : 'border-black/[0.08] hover:border-black/[0.16] hover:bg-black/[0.02]'
                  }`}
                >
                  {/* La letra y no un punto de radio: con cuatro opciones largas,
                      da un ancla para volver a la que estabas leyendo. */}
                  <span
                    className={`grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[12px] font-bold ${
                      seleccionada
                        ? 'bg-brand-primary text-white'
                        : 'bg-black/[0.05] text-black/45'
                    }`}
                  >
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span
                    className={`text-[14.5px] leading-relaxed ${
                      seleccionada ? 'font-medium text-brand-black/90' : 'text-black/70'
                    }`}
                  >
                    {opcion}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <p className="mt-3 text-[13.5px] leading-relaxed text-red-700">{error}</p>
        )}

        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setIndice((i) => Math.max(0, i - 1))}
            disabled={indice === 0}
            className="rounded-xl px-3 py-2.5 text-[13.5px] font-medium text-black/50 transition-colors hover:text-brand-primary disabled:pointer-events-none disabled:opacity-0"
          >
            Anterior
          </button>

          {/* Sin respuesta no se avanza: saltear preguntas convierte el "Corregir"
              en una trampa, porque el puntaje cuenta sobre el total. */}
          <button
            type="button"
            disabled={elegida === null || enviando}
            onClick={() => (esUltima ? corregir() : setIndice((i) => i + 1))}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:bg-black/[0.12] disabled:text-black/35"
          >
            {enviando ? 'Corrigiendo…' : esUltima ? 'Corregir examen' : 'Siguiente'}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * El resultado: puntaje, aprobación y la revisión pregunta por pregunta.
 *
 * La revisión se muestra SIEMPRE, se haya aprobado o no. Reprobado es donde más
 * sirve —es la única forma de saber qué estudiar— y aprobado sigue enseñando.
 * Qué tanto revela lo decidió el servidor: las correcciones de un intento
 * reprobado vienen sin la respuesta correcta.
 */
function Resultado({
  curso,
  resultado,
  onReintentar,
  espera,
}: {
  curso: ReturnType<typeof useAcademia>['cursos'][number];
  resultado: ResultadoExamen;
  onReintentar: () => void;
  espera: { listo: boolean; etiqueta: string };
}) {
  const navigate = useNavigate();
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollAlTope(raiz.current);
  }, []);

  const { aprobo, puntaje, aciertos, total } = resultado;

  return (
    <div ref={raiz} className="mx-auto max-w-content px-4 pb-24 pt-2 sm:px-8 md:pb-8">
      <div>
        <h1 className="text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-brand-black/90 sm:text-[32px]">
          Resultado del examen
        </h1>
      </div>

      <div className="mt-6">
        {/* Veredicto. El verde/ámbar es lo único que cambia: la misma tarjeta para
            los dos casos, porque reprobar no es un error del sistema. */}
        <div
          className={`rounded-2xl border p-5 sm:p-6 ${
            aprobo ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          {/* Ícono ARRIBA y el texto abajo, en vertical
              (ADR-2026-08-25-el-veredicto-del-examen-se-apila).
              En fila, sobre un teléfono el texto arrancaba en una columna de ~250 px y
              el veredicto salía en cuatro renglones cortitos al lado de un cuadrado
              vacío. Apilado, el título y la explicación usan el ancho entero. */}
          <span
            className={`grid h-12 w-12 place-items-center rounded-2xl text-white ${
              aprobo ? 'bg-emerald-600' : 'bg-amber-500'
            }`}
          >
            {aprobo ? (
              <BadgeCheck className="h-6 w-6" strokeWidth={2} />
            ) : (
              <RotateCcw className="h-5 w-5" strokeWidth={2.2} />
            )}
          </span>

          <p
            className={`mt-3.5 text-[20px] font-bold tracking-[-0.01em] ${
              aprobo ? 'text-emerald-900' : 'text-amber-900'
            }`}
          >
            {aprobo ? '¡Aprobaste el curso!' : 'No alcanzó esta vez'}
          </p>
          <p
            className={`mt-1 text-[14px] leading-relaxed ${
              aprobo ? 'text-emerald-800/80' : 'text-amber-900/75'
            }`}
          >
            {aciertos} de {total} correctas — {puntaje}%.{' '}
            {aprobo
              ? `Necesitabas ${curso.puntajeMinimo}%.`
              : `Necesitás ${curso.puntajeMinimo}% para aprobar. Repasá las lecciones y volvé a intentarlo.`}
          </p>

          <div className="mt-5 flex flex-wrap gap-2.5">
            {/* Aprobado, el CTA primario es EL CERTIFICADO
                (ADR-2026-08-25-aprobado-el-cta-es-el-certificado): es el premio del
                examen y el momento en que más se lo quiere. Después sigue disponible
                desde el panel del curso, para siempre. */}
            {aprobo && <BotonCertificado curso={curso} variante="resultado" />}

            {/* "Volver al curso" sólo si NO aprobó: ahí volver es lo que hay que hacer
                —repasar las lecciones antes del próximo intento—. Aprobado, el curso ya
                no tiene nada pendiente y mandar de vuelta ahí compite con el
                certificado por el lugar de la acción principal. */}
            {!aprobo && (
              <button
                type="button"
                onClick={() => navigate(`/academia/${curso.slug}`)}
                className="rounded-xl bg-brand-primary px-4 py-2.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
              >
                Volver al curso
              </button>
            )}

            {/* "Ver todos los cursos" con fondo propio y al lado del primario, no
                como texto suelto en otro renglón: sobre el ámbar (o el verde) de la
                tarjeta, un link gris se leía como una nota al pie y no como la otra
                salida que es. */}
            <button
              type="button"
              onClick={() => navigate('/academia')}
              className="rounded-xl bg-white px-4 py-2.5 text-[13.5px] font-semibold text-black/60 ring-1 ring-black/[0.07] transition-colors hover:text-brand-primary"
            >
              Ver todos los cursos
            </button>

            {/* Repetir es un BOTÓN sólo cuando de verdad se puede. Aprobado no se
                repite (ADR-2026-08-24-el-curso-aprobado-no-se-repite): no queda nada
                que forzar. */}
            {!aprobo && espera.listo && (
              <button
                type="button"
                onClick={onReintentar}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-[13.5px] font-semibold text-black/65 ring-1 ring-black/[0.1] transition-colors hover:text-brand-primary"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Repetir examen
              </button>
            )}
          </div>

          {/* Con la espera corriendo, cuánto falta es un DATO, no una acción: como
              botón deshabilitado ocupaba el lugar de algo que se puede tocar y invitaba
              a intentarlo (ADR-2026-08-25-la-espera-se-cuenta-como-texto-no-como-boton).
              Abajo de los botones y en una línea, que es como se lee un aviso. */}
          {!aprobo && !espera.listo && (
            <p
              className={`mt-3.5 flex items-center gap-1.5 text-[13px] ${
                aprobo ? 'text-emerald-800/70' : 'text-amber-900/65'
              }`}
            >
              <Clock className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              Vas a poder repetir el examen en
              <span className="font-semibold tabular-nums">{espera.etiqueta}</span>
            </p>
          )}
        </div>

        <RevisionRespuestas correcciones={resultado.correcciones} />
      </div>
    </div>
  );
}
